import { Link } from "@tanstack/react-router";

import {
  useCalibrations,
  useMarkCalibrationOutlier,
  useVoidCalibration,
} from "@/lib/api/calibrations";
import { useNutrients } from "@/lib/api/nutrients";
import type { NutrientCalibration } from "@/lib/api/types";
import { ErrorState } from "@/components/error-state";
import { Button } from "@/components/ui/button";

export function ChemistryCalibrationsPage() {
  const q = useCalibrations({ limit: 100 });
  const nutrients = useNutrients();
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No calibration rows yet — they'll start appearing as refill steps
        get recorded.
      </p>
    );
  }
  const nutrientName = (id: string) =>
    nutrients.data?.items.find((n) => n.id === id)?.name;
  return (
    <ul className="divide-y rounded-md border">
      {items.map((c) => (
        <Row key={c.id} row={c} nutrientName={nutrientName(c.nutrient_id)} />
      ))}
    </ul>
  );
}

function Row({
  row,
  nutrientName,
}: {
  row: NutrientCalibration;
  nutrientName?: string;
}) {
  const mark = useMarkCalibrationOutlier(row.id);
  const voidCal = useVoidCalibration(row.id);

  function onVoid() {
    const reason =
      window.prompt("Reason for deleting this calibration?") || "user void";
    voidCal.mutate(reason);
  }

  return (
    <li
      className={
        "flex items-start justify-between gap-4 px-4 py-3 text-sm " +
        (row.voided_at ? "opacity-50" : "")
      }
    >
      <div className="space-y-0.5">
        <p className="font-medium">
          <Link
            to="/chemistry-profile/nutrients/$nutrientId"
            params={{ nutrientId: row.nutrient_id }}
            className="hover:underline"
          >
            {nutrientName ?? (
              <code className="text-xs">{row.nutrient_id.slice(-6)}</code>
            )}
          </Link>
          {row.outlier_flag && (
            <span className="ml-2 text-[10px] uppercase text-amber-600 dark:text-amber-400">
              outlier
            </span>
          )}
          {row.voided_at && (
            <span className="ml-2 text-[10px] uppercase text-muted-foreground">
              voided
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {row.ml_per_gal} ml/gal · {row.batch_size_gal} gal · ΔEC{" "}
          {row.derived_delta_ec_per_ml ?? "—"} · ΔpH{" "}
          {row.derived_delta_ph_per_ml ?? "—"}
        </p>
      </div>
      {!row.voided_at && (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const reason =
                window.prompt(
                  row.outlier_flag
                    ? "Reason for clearing outlier flag?"
                    : "Reason for marking as outlier?",
                ) || (row.outlier_flag ? "clear outlier" : "manual outlier");
              mark.mutate({ outlier: !row.outlier_flag, reason });
            }}
            disabled={mark.isPending}
          >
            {row.outlier_flag ? "Clear outlier" : "Mark outlier"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={onVoid}
            disabled={voidCal.isPending}
          >
            {voidCal.isPending ? "Deleting…" : "Delete"}
          </Button>
        </div>
      )}
    </li>
  );
}
